import { Panel } from 'src/ui/Panel';
import { Button } from 'src/ui/Button';
import { Icon } from 'src/ui/Icon';
import { EmptyState } from 'src/ui/EmptyState';
import { Textarea } from 'src/ui/Textarea';

interface SentPromptPanelProps {
  /** Exact system prompt the backend sent on the last turn (null until first send). */
  systemPrompt: string | null;
  previewCount?: number;
  /** Whether the panel is collapsed to a 46px rail. */
  collapsed: boolean;
  /** Toggle collapsed ↔ expanded. */
  onToggle: () => void;
}

/**
 * Read-only view of the actual system prompt sent to the matchmaker on the
 * last turn — the assembled text (persona + mode blocks + the impersonated
 * user's rendered profile, topics, pool, feedback). Populates after the first
 * message is sent in the chat.
 *
 * Collapsed state: 46px-wide rail with a vertical label and expand affordance.
 */
const SentPromptPanel = ({
  systemPrompt,
  previewCount = 0,
  collapsed,
  onToggle,
}: SentPromptPanelProps) => {
  if (collapsed) {
    return (
      <div
        className="panel"
        style={{
          width: 46,
          flexShrink: 0,
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={onToggle}
        title="Show sent prompt"
      >
        <div
          className="row"
          style={{
            height: 45,
            justifyContent: 'center',
            width: '100%',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Icon
            name="chevron-left"
            size={16}
            style={{ color: 'var(--text-muted)' }}
          />
        </div>
        <div
          className="grow"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            Sent to matchmaker
          </span>
        </div>
      </div>
    );
  }

  const chars = systemPrompt ? systemPrompt.length : 0;

  const headerRight = (
    <div className="row gap-2">
      <span
        className="mono"
        style={{
          fontSize: 'var(--fs-2xs)',
          fontWeight: 600,
          color: 'var(--text-muted)',
          background: 'var(--bg-muted)',
          borderRadius: 99,
          padding: '2px 8px',
        }}
      >
        {chars.toLocaleString()} chars
      </span>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        title="Collapse"
        onClick={onToggle}
      >
        <Icon name="chevron-right" size={16} />
      </Button>
    </div>
  );

  return (
    <Panel
      title="Sent to matchmaker"
      icon="hash"
      headerRight={headerRight}
      className="grow"
      bodyStyle={{
        padding: '10px 14px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div className="col gap-2" style={{ height: '100%' }}>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-muted)',
            flexShrink: 0,
          }}
        >
          Exact system prompt the backend sent on the last turn
          {previewCount > 0 && (
            <span
              className="row gap-1"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'var(--purple-text)',
                marginLeft: 6,
              }}
            >
              <Icon name="eye" size={12} />
              {previewCount} preview override{previewCount > 1 ? 's' : ''}
            </span>
          )}
        </p>
        {systemPrompt == null ? (
          <div
            className="grow"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1.5px dashed var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              padding: 20,
            }}
          >
            <EmptyState
              icon="hash"
              title="No prompt captured yet"
              desc="Send a message in the chat to capture the prompt."
            />
          </div>
        ) : (
          <Textarea
            readOnly
            value={systemPrompt}
            spellCheck={false}
            className="mono"
            style={{
              flex: 1,
              fontSize: 'var(--fs-2xs)',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              resize: 'none',
              background: 'var(--bg-subtle)',
              color: 'var(--text-secondary)',
            }}
          />
        )}
      </div>
    </Panel>
  );
};

export default SentPromptPanel;
